import React, { useRef, useState, useCallback } from 'react';

export function generateDates(startStr, endStr) {
  const dates = [];
  const cur = new Date(startStr + 'T00:00:00Z');
  const end = new Date(endStr + 'T00:00:00Z');
  while (cur <= end) {
    dates.push(cur.toISOString().slice(0, 10));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return dates;
}

export function generateTimeSlots(hoursStart, hoursEnd, slotMinutes) {
  const slots = [];
  const [sh, sm] = hoursStart.split(':').map(Number);
  const [eh, em] = hoursEnd.split(':').map(Number);
  const startMin = sh * 60 + sm;
  const endMin = eh * 60 + em;
  for (let m = startMin; m < endMin; m += (slotMinutes || 30)) {
    slots.push(String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0'));
  }
  return slots;
}

function localToUTC(dateStr, timeStr, timezone) {
  const [hh, mm] = timeStr.split(':').map(Number);
  const guessUTC = new Date(dateStr + 'T' + timeStr + ':00Z');
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(guessUTC);
    const localH = parseInt(parts.find(p => p.type === 'hour')?.value ?? hh);
    const localM = parseInt(parts.find(p => p.type === 'minute')?.value ?? mm);
    const diffMs = ((hh - localH) * 60 + (mm - localM)) * 60000;
    return new Date(guessUTC.getTime() + diffMs).toISOString();
  } catch {
    return guessUTC.toISOString();
  }
}

export function slotKeyToISO(key, timezone) {
  const [dateStr, timeStr] = key.split('_');
  return localToUTC(dateStr, timeStr, timezone);
}

export function isoToSlotKey(iso, timezone) {
  try {
    const d = new Date(iso);
    const datePart = new Intl.DateTimeFormat('sv-SE', {
      timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(d);
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(d);
    const hh = (parts.find(p => p.type === 'hour')?.value ?? '00').padStart(2, '0');
    const mm = (parts.find(p => p.type === 'minute')?.value ?? '00').padStart(2, '0');
    return `${datePart}_${hh}:${mm}`;
  } catch {
    return null;
  }
}

function fmtTimeLabel(timeStr) {
  const [hh, mm] = timeStr.split(':').map(Number);
  const ampm = hh >= 12 ? 'PM' : 'AM';
  const h12 = hh % 12 || 12;
  return `${h12}${mm > 0 ? ':' + String(mm).padStart(2, '0') : ''} ${ampm}`;
}

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtDateLabel(dateStr) {
  const d = new Date(dateStr + 'T12:00:00Z');
  return { day: DAY_ABBR[d.getUTCDay()], date: `${MON_ABBR[d.getUTCMonth()]} ${d.getUTCDate()}` };
}

export default function AvailabilityGrid({
  dateRangeStart,
  dateRangeEnd,
  workingHoursStart,
  workingHoursEnd,
  slotMinutes = 30,
  timezone = 'UTC',
  mode = 'select',
  selected,
  onSelectionChange,
  hostAvailKeys,
  studentAvailKeys,
  onBookSlot,
  readOnly = false,
  bookingBusy = false,
}) {
  const dates = generateDates(dateRangeStart, dateRangeEnd);
  const slots = generateTimeSlots(workingHoursStart, workingHoursEnd, slotMinutes);
  const isDraggingRef = useRef(false);
  const selectionTypeRef = useRef('add');
  const [displaySelected, setDisplaySelected] = useState(() => new Set(selected));
  const pendingRef = useRef(new Set(selected));

  const committedRef = useRef(selected);
  if (selected !== committedRef.current) {
    committedRef.current = selected;
    pendingRef.current = new Set(selected);
    setDisplaySelected(new Set(selected));
  }

  const startDrag = useCallback((key) => {
    if (readOnly || mode !== 'select') return;
    isDraggingRef.current = true;
    selectionTypeRef.current = pendingRef.current.has(key) ? 'remove' : 'add';
    const next = new Set(pendingRef.current);
    if (selectionTypeRef.current === 'add') next.add(key); else next.delete(key);
    pendingRef.current = next;
    setDisplaySelected(new Set(next));
  }, [readOnly, mode]);

  const duringDrag = useCallback((key) => {
    if (!isDraggingRef.current || readOnly || mode !== 'select') return;
    const next = new Set(pendingRef.current);
    if (selectionTypeRef.current === 'add') next.add(key); else next.delete(key);
    pendingRef.current = next;
    setDisplaySelected(new Set(next));
  }, [readOnly, mode]);

  const endDrag = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    onSelectionChange?.(new Set(pendingRef.current));
  }, [onSelectionChange]);

  const handleTouchMove = useCallback((e) => {
    if (!isDraggingRef.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const key = el?.dataset?.akey;
    if (key) duringDrag(key);
  }, [duringDrag]);

  const numCols = dates.length + 1;

  return (
    <div
      className="agrid-scroll"
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
      onTouchEnd={endDrag}
      onTouchMove={handleTouchMove}
    >
      <div
        className="agrid"
        style={{ gridTemplateColumns: `56px repeat(${dates.length}, minmax(64px, 1fr))` }}
      >
        <div className="agrid-corner" />
        {dates.map(date => {
          const { day, date: dateLabel } = fmtDateLabel(date);
          return (
            <div key={`h-${date}`} className="agrid-col-header">
              <span className="agrid-day">{day}</span>
              <span className="agrid-date">{dateLabel}</span>
            </div>
          );
        })}

        {slots.map(slot => {
          return [
            <div key={`l-${slot}`} className="agrid-row-label">{fmtTimeLabel(slot)}</div>,
            ...dates.map(date => {
              const key = `${date}_${slot}`;
              let cls = 'agrid-cell';
              let isOverlap = false;
              let isHost = false;
              let isStudent = false;

              if (mode === 'select') {
                if (displaySelected.has(key)) cls += ' agrid-cell--selected';
              } else {
                isHost = hostAvailKeys?.has(key);
                isStudent = studentAvailKeys?.has(key);
                isOverlap = isHost && isStudent;
                if (isOverlap) cls += ' agrid-cell--overlap';
                else if (isHost) cls += ' agrid-cell--host';
                else if (isStudent) cls += ' agrid-cell--student';
              }

              return (
                <div
                  key={key}
                  className={cls}
                  data-akey={key}
                  onMouseDown={mode === 'select' && !readOnly ? (e) => { e.preventDefault(); startDrag(key); } : undefined}
                  onMouseEnter={mode === 'select' && !readOnly ? () => duringDrag(key) : undefined}
                  onTouchStart={mode === 'select' && !readOnly ? (e) => { e.preventDefault(); startDrag(key); } : undefined}
                >
                  {mode === 'overlap' && isOverlap && onBookSlot && (
                    <button
                      className="agrid-book-btn"
                      onClick={() => onBookSlot(key)}
                      disabled={bookingBusy}
                    >
                      {bookingBusy ? '…' : 'Book'}
                    </button>
                  )}
                </div>
              );
            })
          ];
        })}
      </div>
    </div>
  );
}
